/*
 * Copyright (C) 2026 Das Land Schleswig-Holstein vertreten durch den
 * Ministerpräsidenten des Landes Schleswig-Holstein
 * Staatskanzlei
 * Abteilung Digitalisierung und zentrales IT-Management der Landesregierung
 *
 * Lizenziert unter der EUPL, Version 1.2 oder - sobald
 * diese von der Europäischen Kommission genehmigt wurden -
 * Folgeversionen der EUPL ("Lizenz");
 * Sie dürfen dieses Werk ausschließlich gemäß
 * dieser Lizenz nutzen.
 * Eine Kopie der Lizenz finden Sie hier:
 *
 * https://joinup.ec.europa.eu/collection/eupl/eupl-text-eupl-12
 *
 * Sofern nicht durch anwendbare Rechtsvorschriften
 * gefordert oder in schriftlicher Form vereinbart, wird
 * die unter der Lizenz verbreitete Software "so wie sie
 * ist", OHNE JEGLICHE GEWÄHRLEISTUNG ODER BEDINGUNGEN -
 * ausdrücklich oder stillschweigend - verbreitet.
 * Die sprachspezifischen Genehmigungen und Beschränkungen
 * unter der Lizenz sind dem Lizenztext zu entnehmen.
 */
import { Component, computed, input, OnDestroy, signal } from '@angular/core';
import { debounce, DebouncedFunc } from 'lodash-es';
import { AriaLiveRegionComponent } from '../aria-live-region/aria-live-region.component';
import { ButtonComponent, ButtonVariants } from '../button/button.component';
import { ButtonIconName } from '../icon/icons';
import { TooltipDirective } from '../tooltip/tooltip.directive';

export const COPY_RESET_DELAY_IN_MS: number = 1500;

@Component({
  selector: 'ods-copy-button',
  imports: [ButtonComponent, TooltipDirective, AriaLiveRegionComponent],
  host: { class: 'block w-fit' },
  template: `<ods-button
      [variant]="variant()"
      size="fit"
      [icon]="icon()"
      [iconConfig]="{ size: 'small' }"
      [tooltip]="currentTooltip()"
      tooltipAriaType="aria-label"
      [dataTestId]="dataTestId()"
      (clicked)="copy()"
    />
    <ods-aria-live-region [text]="liveMessage()" />`,
})
export class CopyButtonComponent implements OnDestroy {
  public readonly value = input.required<string>();
  public readonly variant = input<ButtonVariants['variant']>('outline');
  public readonly copyTooltip = input<string>('Text kopieren');
  public readonly copiedTooltip = input<string>('Kopiert');
  public readonly dataTestId = input<string>('');

  protected readonly copied = signal<boolean>(false);
  protected readonly liveMessage = signal<string>('');

  protected readonly icon = computed<ButtonIconName>(() => (this.copied() ? 'check' : 'copy'));
  protected readonly currentTooltip = computed<string>(() => (this.copied() ? this.copiedTooltip() : this.copyTooltip()));

  public async copy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(this.value());
    } catch {
      return;
    }
    this.showCopiedFeedback();
  }

  private showCopiedFeedback(): void {
    this.copied.set(true);
    this.liveMessage.set(this.copiedTooltip());
    this._debouncedReset();
  }

  private reset(): void {
    this.copied.set(false);
    this.liveMessage.set('');
  }

  readonly _debouncedReset: DebouncedFunc<() => void> = debounce(() => this.reset(), COPY_RESET_DELAY_IN_MS);

  ngOnDestroy(): void {
    this._debouncedReset.cancel();
  }
}
